<template>
  <main class="notifications bb">
    <section>
      <PageBackButton />

      <h1 class="pg_title">{{ $_translations.value.page_notification_title }}</h1>

      <div class="flex-row notif_top">
        <p class="notif_numb">
          {{ totalNotifications }}
          {{ $_translations.value.page_notification_number_of_notifications }}
        </p>

        <div
          v-if="hasUnreadNotifications"
          class="flex-row mark_read"
          @click="notificationStore.dispatchMarkAllNotificationAsRead"
        >
          <BaseImage
            src="/static/images/Checkmark.svg"
            alt="checkmark notifications"
          />

          <p>{{ $_translations.value.page_notification_button_mark_as_read }}</p>
        </div>
      </div>
    </section>

    <section class="boxes_sect">
      <NotificationItem
        v-for="notification in filteredNotificationList"
        :key="notification.id"
        :item="notification"
        @read:notification="handleReadNotification(notification)"
      />

      <BaseButton
        v-if="hasNotifications"
        :disabled="submitting"
        @click:button="handleClearAllNotifications"
      >
        {{ $_translations.value.page_notification_button_clear }}
      </BaseButton>
    </section>
  </main>

  <BaseLoaderModal v-if="isLoaderModalOpen" />

  <CompletedAchievementModal
    v-else-if="isAchievementModalOpen"
    :data="modalProps.data"
    :play-sound="modalProps.playSound"
  />

  <CompletedLevelModal
    v-else-if="isLevelModalOpen"
    :data="modalProps.data"
    :play-sound="modalProps.playSound"
    @redirect:levelsPage="handleRedirectToLevelsPageFromLevelModal"
  />

  <BundleDiscountBonusModal
    v-else-if="isBundleDiscountBonusModalOpen"
    :start-date="modalProps.startDate"
    :end-date="modalProps.endDate"
    :multiplier="modalProps.multiplier"
    @redirect:storePage="handleRedirectToStorePageFromBundleDiscountBonusModal"
  />

  <PointsMultiplierBonusModal
    v-else-if="isPointsMultiplierBonusModalOpen"
    :start-date="modalProps.startDate"
    :end-date="modalProps.endDate"
    :multiplier="modalProps.multiplier"
    @start:newGame="handleStartNewGameFromPointsMultiplierBonusModal"
  />

  <GameBonusModal
    v-else-if="isGameBonusModalOpen"
    :game-type="modalProps.gameType"
    :can-play="modalProps.canPlay"
    @start:newBonusGame="handleStartNewBonusGameFromGameBonusModal"
  />

  <BonusPhaseDailyModal
    v-else-if="isDailyBonusModalOpen"
    :prize-value="modalProps.prizeValue"
    :prize-type="modalProps.prizeType"
    @redirect:homePage="handleRedirectToHomePageFromDailyBonusModal"
  />

  <SuccessInviteModal
    v-else-if="isSuccessInviteModalOpen"
    :prize-value="modalProps.prizeValue"
    :prize-type="modalProps.prizeType"
  />

  <ZeroBalanceGameModal
    v-else-if="isZeroBalanceGameModalOpen"
    @redirect:storePage="handleRedirectToStorePageFromZeroBalanceGameModal"
  />

  <PreGameSection
    v-else-if="isPreGameVisible"
    @hide:preGame="hidePreGame"
  />

  <PreGamePuzzleModal
    v-else-if="isPreGamePuzzleModalOpen"
    @start:puzzleGame="handleStartPuzzleGame"
  />

  <PreGameLanternModal
    v-else-if="isPreGameLanternModalOpen"
    @start:lanternGame="handleStartLanternGame"
  />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import PageBackButton from '@/components/button/PageBackButton.vue';
import BaseImage from '@/components/image/BaseImage.vue';
import BaseButton from '@/components/button/BaseButton.vue';
import BaseLoaderModal from '@/components/modal/loader/BaseLoaderModal.vue';
import CompletedAchievementModal
  from '@/components/modal/achievement/CompletedAchievementModal.vue';
import CompletedLevelModal from '@/components/modal/level/CompletedLevelModal.vue';
import BundleDiscountBonusModal from '@/components/modal/bonus/BundleDiscountBonusModal.vue';
import PointsMultiplierBonusModal from '@/components/modal/bonus/PointsMultiplierBonusModal.vue';
import BonusPhaseDailyModal from '@/components/modal/BonusPhaseDailyModal.vue';
import GameBonusModal from '@/components/modal/bonus/GameBonusModal.vue';
import SuccessInviteModal from '@/components/modal/invite/SuccessInviteModal.vue';
import ZeroBalanceGameModal from '@/components/modal/game/ZeroBalanceGameModal.vue';
import PreGameSection from '@/components/section/PreGameSection.vue';
import PreGamePuzzleModal from '@/views/bonusGame/components/modal/PreGamePuzzleModal.vue';
import PreGameLanternModal from '@/views/bonusGame/components/modal/PreGameLanternModal.vue';
import NotificationItem from '@/views/notification/NotificationItem.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import useLevelStore from '@/store/modules/level.js';
import useBonusPhaseStore from '@/store/modules/bonusPhase.js';
import useNotificationStore from '@/store/modules/notification.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';
import usePreGameHook from '@/hooks/usePreGame.js';
import { apiGetBonusGameStatus } from '@/services/game.js';
import { constructNotification } from '@/helpers/notification.js';
import { notificationTypeEnum } from '@/constants/notification.js';
import { bonusGameTypeEnum } from '@/constants/game.js';

const appSettings = useAppSettingsStore();
const authStore = useAuthStore();
const levelStore = useLevelStore();
const bonusPhaseStore = useBonusPhaseStore();
const notificationStore = useNotificationStore();

const { redirectTo, redirectToStorePage, redirectToHomePage } = usePageNavigationHook();
const [isLoaderModalOpen, openLoaderModal, closeLoaderModal] = useModalHook();
const [isAchievementModalOpen, openAchievementModal] = useModalHook();
const [isLevelModalOpen, openLevelModal, closeLevelModal] = useModalHook();
const [isBundleDiscountBonusModalOpen, openBundleDiscountBonusModal, closeBundleDiscountBonusModal] = useModalHook();
const [isPointsMultiplierBonusModalOpen, openPointsMultiplierBonusModal, closePointsMultiplierBonusModal] = useModalHook();
const [isDailyBonusModalOpen, openDailyBonusModal, closeDailyBonusModal] = useModalHook();
const [isGameBonusModalOpen, openGameBonusModal, closeGameGameModal] = useModalHook();
const [isSuccessInviteModalOpen, openSuccessInviteModal] = useModalHook();
const [isZeroBalanceGameModalOpen, openZeroBalanceGameModal, closeZeroBalanceGameModal] = useModalHook();
const [isPreGamePuzzleModalOpen, openPreGamePuzzleModal, closePreGamePuzzleModal] = useModalHook();
const [isPreGameLanternModalOpen, openPreGameLanternModal, closePreGameLanternModal] = useModalHook();
const { isPreGameVisible, launchNewGameFlow, hidePreGame } = usePreGameHook();

const modalProps = ref({});
const submitting = ref(false);

const achievementPerNotificationText = computed(() => authStore.achievementsConfig.list.reduce((acc, achievement) => {
  achievement.detailsList.forEach((details) => {
    acc[`${ achievement.__category }_${ details.achievementLevelName }`] = {
      details,
      achievementName: achievement.achievementName,
      __category: achievement.__category,
    };
  });

  return acc;
}, {}));
const formattedNotificationList = computed(() =>
  notificationStore.notificationList.map((notification) =>
    constructNotification(notification, achievementPerNotificationText.value, levelStore.levelList),
  ),
);
const filteredNotificationList = computed(() =>
  formattedNotificationList.value.filter(({ type }) => (type !== notificationTypeEnum.BONUS_BUNDLE_DISCOUNT) || !appSettings.isStoreDisabled),
);
const totalNotifications = computed(() => filteredNotificationList.value.length);
const hasNotifications = computed(() => totalNotifications.value > 0);
const hasUnreadNotifications = computed(() => filteredNotificationList.value.some(({ isUnread }) => isUnread));

const resetModalState = () => {
  modalProps.value = {};
};

const handleReadBonusGameNotification = async (notification) => {
  try {
    openLoaderModal({ hideManually: true });

    const gameId = notification.gameId;

    const { canPlay } = await apiGetBonusGameStatus({ queryParams: { gameId } });

    await closeLoaderModal({ immediate: true });

    let gameType;
    if (notificationTypeEnum.GAME_PUZZLE === notification.type) {
      gameType = bonusGameTypeEnum.BONUS_GAME_PUZZLE;
    } else if (notificationTypeEnum.GAME_LANTERNS === notification.type) {
      gameType = bonusGameTypeEnum.BONUS_GAME_LANTERNS;
    }

    modalProps.value = { gameType, canPlay };

    bonusPhaseStore.setBonusPhaseGameConfig({
      gameId,
      prizeValue: notification.prizeValue,
    });

    openGameBonusModal({
      showImmediate: true,
      afterHideCallback: () => {
        bonusPhaseStore.resetBonusPhaseGameConfigState();

        resetModalState();
      },
    });
  } catch {
    await closeLoaderModal();
  }
};

const handleReadNotification = (notification) => {
  if (notification.isUnread) {
    notificationStore.dispatchMarkNotificationAsRead(notification.id);
  }

  if ([notificationTypeEnum.UPDATE_PROFILE, notificationTypeEnum.EMAIL_VERIFICATION].includes(notification.type)) {
    redirectTo('ProfileEdit');
  } else if (notificationTypeEnum.ACHIEVEMENT === notification.type) {
    modalProps.value = {
      data: {
        __category: achievementPerNotificationText.value[notification.text]?.__category,
        achievementName: achievementPerNotificationText.value[notification.text]?.achievementName,
        prizeValue: achievementPerNotificationText.value[notification.text]?.details?.prizeValue,
        prizeType: achievementPerNotificationText.value[notification.text]?.details?.prizeType,
        threshold: achievementPerNotificationText.value[notification.text]?.details?.threshold,
      },
      playSound: false,
    };
    openAchievementModal({ afterHideCallback: resetModalState });
  } else if (notificationTypeEnum.LEVEL_UP === notification.type) {
    modalProps.value = {
      data: {
        __level: notification.userLevelNumber,
        prizeValue: notification.prizeValue,
      },
      playSound: false,
    };
    openLevelModal({ afterHideCallback: resetModalState });
  } else if (notificationTypeEnum.BONUS_BUNDLE_DISCOUNT === notification.type) {
    modalProps.value = {
      startDate: notification.startDate,
      endDate: notification.endDate,
      multiplier: notification.multiplier,
    };
    openBundleDiscountBonusModal({ afterHideCallback: resetModalState });
  } else if (notificationTypeEnum.BONUS_GAME_POINTS === notification.type) {
    modalProps.value = notification;
    openPointsMultiplierBonusModal({ afterHideCallback: resetModalState });
  } else if (notificationTypeEnum.BONUS_DAILY_VISIT === notification.type) {
    modalProps.value = notification;
    openDailyBonusModal({ afterHideCallback: resetModalState });
  } else if ([notificationTypeEnum.GAME_PUZZLE, notificationTypeEnum.GAME_LANTERNS].includes(notification.type)) {
    handleReadBonusGameNotification(notification);
  } else if (notificationTypeEnum.REFERRAL_REGISTRATION === notification.type) {
    modalProps.value = {
      prizeValue: notification.prizeValue,
      prizeType: notification.prizeType,
    };
    openSuccessInviteModal({ afterHideCallback: resetModalState });
  } else if (notificationTypeEnum.BALANCE_ZERO_GAMES === notification.type) {
    openZeroBalanceGameModal({ afterHideCallback: resetModalState });
  }
};

const handleClearAllNotifications = async () => {
  submitting.value = true;

  await notificationStore.dispatchClearAllNotifications();

  submitting.value = false;
};

const handleRedirectToLevelsPageFromLevelModal = async () => {
  await closeLevelModal({ immediate: true });

  resetModalState();
  redirectTo('Levels');
};

const handleRedirectToStorePageFromBundleDiscountBonusModal = async () => {
  await closeBundleDiscountBonusModal({ immediate: true });

  resetModalState();
  redirectToStorePage();
};

const handleStartNewGameFromPointsMultiplierBonusModal = async () => {
  await closePointsMultiplierBonusModal({ immediate: true });

  resetModalState();
  launchNewGameFlow();
};

const handleRedirectToHomePageFromDailyBonusModal = async () => {
  await closeDailyBonusModal({ immediate: true });

  resetModalState();
  redirectToHomePage();
};

const handleStartNewBonusGameFromGameBonusModal = async (gameType) => {
  await closeGameGameModal({ immediate: true });

  resetModalState();

  const modalArgs = {
    showImmediate: true,
    afterHideCallback: () => {
      bonusPhaseStore.resetBonusPhaseGameConfigState();
    },
  };

  if (gameType === bonusGameTypeEnum.BONUS_GAME_PUZZLE) {
    openPreGamePuzzleModal(modalArgs);
  } else if (gameType === bonusGameTypeEnum.BONUS_GAME_LANTERNS) {
    openPreGameLanternModal(modalArgs);
  }
};

const handleRedirectToStorePageFromZeroBalanceGameModal = async () => {
  await closeZeroBalanceGameModal({ immediate: true });

  resetModalState();
  redirectToStorePage();
};

const handleStartPuzzleGame = async () => {
  await closePreGamePuzzleModal({ immediate: true });

  resetModalState();

  redirectTo('BonusGamePuzzle');
};

const handleStartLanternGame = async () => {
  await closePreGameLanternModal({ immediate: true });

  resetModalState();

  redirectTo('BonusGameLantern');
};

onMounted(() => {
  notificationStore.dispatchRetrieveNotifications();
});
</script>
